import { ChangeEvent, HTMLAttributes, MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import SearchIcon from '@mui/icons-material//Search';
import { Autocomplete, Box, IconButton, InputAdornment, TextField, Typography } from '@mui/material';
import clsx from 'clsx';
import { useNavigate } from 'react-router-dom';
import { fundSummaryUrl } from 'common/config/urls';
import { normalizePinnedItems, PINNED_FUNDS_KEY } from 'common/constants/userPreferences';
import { useLocalStorageByUser } from 'common/hooks';
import { useStore } from 'common/store';
import { OutlinedPushPin, PushPin } from 'icons';
import { getObjectValue } from 'utilities';
import { FundFilterProps } from './FundFilter.props';
import { classes, Root } from './FundFilter.styles';
import { ItemOption } from './FundFilter.types';

const FundFilter = ({ open, setOpenSidebar, updatePins }: FundFilterProps) => {
  const navigate = useNavigate();

  const [storeValue] = useStore();
  const { fundList = [], firmInfo } = getObjectValue(storeValue);

  const [pinnedItemsRaw] = useLocalStorageByUser(PINNED_FUNDS_KEY, []);
  const pinnedItems = useMemo(() => normalizePinnedItems(pinnedItemsRaw), [pinnedItemsRaw]);

  const [options, setOptions] = useState<ItemOption[]>([]);

  const itemList = useMemo(() => fundList, [fundList]);

  const isPinned = useCallback(
    (option: ItemOption) => pinnedItems.find(o => o.value === option.value) ?? false,
    [pinnedItems]
  );

  useEffect(() => {
    if (Array.isArray(itemList) && itemList.length) {
      const tmpOptions: ItemOption[] = [];

      itemList.forEach(fund => {
        tmpOptions.push({
          value: fund.id,
          label: fund.name,
        });
      });

      setOptions(tmpOptions);
    }
  }, [itemList]);

  const updateOption = (e: MouseEvent, option: ItemOption) => {
    e.stopPropagation();

    let tmpOptions: ItemOption[] = [];

    if (isPinned(option)) {
      tmpOptions = pinnedItems.filter(o => o.value !== option.value) ?? [];
    } else {
      tmpOptions = [...pinnedItems, option];
    }

    updatePins(tmpOptions);
  };

  const handleChange = (e: ChangeEvent, option: ItemOption | null) => {
    e.stopPropagation();
    if (!option) {
      return;
    }

    const selectedItem = itemList.find(f => f.id === option.value);

    if (firmInfo && selectedItem) {
      const summaryUrl = fundSummaryUrl(firmInfo.slug, selectedItem.slug);
      navigate(summaryUrl);
    }
  };

  const renderInput = (params: any) => (
    <TextField
      {...params}
      fullWidth
      placeholder="Find a fund"
      slotProps={{
        input: {
          ...params.InputProps,
          classes: {
            root: classes.input,
            notchedOutline: classes.notchedOutline,
            focused: classes.focused,
          },
          endAdornment: (
            <InputAdornment position="end">
              <SearchIcon className={classes.icon} />
            </InputAdornment>
          ),
        },
      }}
      onMouseDownCapture={(e: MouseEvent) => e.stopPropagation()}
      variant="outlined"
    />
  );

  const renderOption = (props: HTMLAttributes<HTMLLIElement>, option: ItemOption) => (
    <li {...props} key={option.value}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
        }}>
        <IconButton
          onClick={e => {
            e.stopPropagation();
            updateOption(e, option);
          }}>
          {isPinned(option) ? <PushPin fontSize="small" /> : <OutlinedPushPin fontSize="small" />}
        </IconButton>
        <Typography>{option.label}</Typography>
      </Box>
    </li>
  );

  return (
    <Root className={clsx(classes.root, { open })}>
      <Autocomplete
        id="funds-filter"
        options={options}
        getOptionLabel={(option: ItemOption) => option.label}
        renderInput={renderInput}
        renderOption={renderOption}
        onChange={handleChange}
        onClose={() => setOpenSidebar(false)}
        clearOnEscape
        autoHighlight
        autoSelect
      />
    </Root>
  );
};

export default FundFilter;
